import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useColoring } from "./ColoringContext.js";
import { coloringApi } from "./api.js";
import { AiButton, SectionCard, useAction } from "./fields.js";
import { CreateBookModal } from "./CreateBookModal.js";
import { AUDIENCE_OPTIONS, DIFFICULTY_OPTIONS, TRIM_OPTIONS, type Audience, type Difficulty } from "./types.js";

/** Overview: the book's setup (editable), its concept and brief, and workflow progress. */
export function OverviewPanel() {
  const { projectId, state, saveBook, reload } = useColoring();
  const toast = useToast();
  const { busy, run } = useAction();
  const { project, book, characters, pages } = state;

  const [title, setTitle] = useState(project.title);
  const [subtitle, setSubtitle] = useState(project.subtitle ?? "");
  const [description, setDescription] = useState(project.description ?? "");
  const [concept, setConcept] = useState(book.concept ?? "");
  const [brief, setBrief] = useState(book.brief ?? "");
  const [pageCount, setPageCount] = useState(book.pageCount);
  const [customW, setCustomW] = useState(book.trimWidth);
  const [customH, setCustomH] = useState(book.trimHeight);
  const [creating, setCreating] = useState(false);

  useEffect(() => setConcept(book.concept ?? ""), [book.concept]);
  useEffect(() => setBrief(book.brief ?? ""), [book.brief]);

  const hasConcept = concept.trim().length > 0 || !!(project.description ?? "").trim();

  const saveProject = async (patch: { title?: string; subtitle?: string | null; description?: string | null }) => {
    try {
      await api.updateProject(projectId, patch);
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save.");
    }
  };

  const developBrief = () =>
    run(
      "brief",
      async () => {
        if (concept !== (book.concept ?? "")) await saveBook({ concept });
        await coloringApi.developConcept(projectId);
        await reload();
      },
      "Creative brief written - edit it as you like."
    );

  const generated = pages.filter((p) => p.currentVariantId).length;
  const approved = pages.filter((p) => p.status === "approved").length;
  const stepsDone = [
    !!(book.concept && book.concept.trim()),
    !!(book.brief && book.brief.trim()),
    characters.length > 0,
    pages.length > 0,
    generated > 0,
    approved > 0
  ];

  return (
    <div className="coloring-scroll">
      <div className="coloring-grid">
        <section className="card hero">
          <div className="row gap-2" style={{ marginBottom: 10, flexWrap: "wrap" }}>
            <span className="chip chip-accent">{AUDIENCE_OPTIONS.find((a) => a.id === book.audience)?.label}</span>
            <span className="chip">{DIFFICULTY_OPTIONS.find((d) => d.id === book.difficulty)?.label}</span>
            <span className="chip">{book.trimWidth} × {book.trimHeight} in</span>
            <span className="chip">{book.pageCount} pages planned</span>
          </div>
          <input
            className="input coloring-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => title.trim() && title !== project.title && void saveProject({ title: title.trim() })}
            aria-label="Title"
          />
          <input
            className="input"
            style={{ marginTop: 8 }}
            value={subtitle}
            placeholder="Subtitle"
            onChange={(e) => setSubtitle(e.target.value)}
            onBlur={() => subtitle !== (project.subtitle ?? "") && void saveProject({ subtitle: subtitle.trim() || null })}
            aria-label="Subtitle"
          />
          <textarea
            className="textarea"
            style={{ marginTop: 8 }}
            rows={2}
            value={description}
            placeholder="Description"
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => description !== (project.description ?? "") && void saveProject({ description: description.trim() || null })}
            aria-label="Description"
          />
          <div className="row gap-2" style={{ marginTop: 16, flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={() => setCreating(true)} disabled={!hasConcept} title={!hasConcept ? "Write a concept first" : undefined}>
              <Icon name="sparkles" size={15} /> Create My Coloring Book
            </button>
            <span className="faint" style={{ fontSize: 12 }}>Plans the whole book for your review. No images are generated.</span>
          </div>
        </section>

        <SectionCard icon="settings" title="Book setup">
          <div className="field">
            <label className="label">Intended audience</label>
            <div className="choice-row">
              {AUDIENCE_OPTIONS.map((a) => (
                <button key={a.id} type="button" className={`choice ${book.audience === a.id ? "on" : ""}`} onClick={() => void saveBook({ audience: a.id as Audience })}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row gap-3" style={{ flexWrap: "wrap" }}>
            <div className="grow field">
              <label className="label">Difficulty</label>
              <select className="select" value={book.difficulty} onChange={(e) => void saveBook({ difficulty: e.target.value as Difficulty })}>
                {DIFFICULTY_OPTIONS.map((d) => (
                  <option key={d.id} value={d.id}>{d.label}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ width: 140 }}>
              <label className="label">Coloring pages</label>
              <input
                className="input"
                type="number"
                min={1}
                max={200}
                value={pageCount}
                onChange={(e) => setPageCount(Number(e.target.value))}
                onBlur={() => {
                  const n = Math.max(1, Math.min(200, Math.round(pageCount) || book.pageCount));
                  setPageCount(n);
                  if (n !== book.pageCount) void saveBook({ pageCount: n });
                }}
              />
            </div>
            <div className="grow field">
              <label className="label">Trim size</label>
              <select
                className="select"
                value={book.trim}
                onChange={(e) => {
                  const preset = TRIM_OPTIONS.find((t) => t.id === e.target.value);
                  if (!preset) return;
                  void saveBook({
                    trim: preset.id,
                    trimWidth: preset.id === "custom" ? customW : preset.width,
                    trimHeight: preset.id === "custom" ? customH : preset.height
                  });
                }}
              >
                {TRIM_OPTIONS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            {book.trim === "custom" && (
              <>
                <div className="field" style={{ width: 100 }}>
                  <label className="label">Width (in)</label>
                  <input className="input" type="number" step={0.125} min={2} max={20} value={customW} onChange={(e) => setCustomW(Number(e.target.value))} onBlur={() => void saveBook({ trimWidth: customW })} />
                </div>
                <div className="field" style={{ width: 100 }}>
                  <label className="label">Height (in)</label>
                  <input className="input" type="number" step={0.125} min={2} max={20} value={customH} onChange={(e) => setCustomH(Number(e.target.value))} onBlur={() => void saveBook({ trimHeight: customH })} />
                </div>
              </>
            )}
          </div>
        </SectionCard>

        <SectionCard
          icon="sparkles"
          title="Concept"
          actions={
            <AiButton busy={busy === "brief"} onClick={() => void developBrief()} disabled={!hasConcept}>
              Develop Concept
            </AiButton>
          }
        >
          <textarea
            className="textarea"
            rows={4}
            value={concept}
            placeholder="Describe the book idea in your own words."
            onChange={(e) => setConcept(e.target.value)}
            onBlur={() => concept !== (book.concept ?? "") && void saveBook({ concept })}
          />
          <label className="label" style={{ marginTop: 14 }}>Creative brief</label>
          <textarea
            className="textarea"
            rows={6}
            value={brief}
            placeholder="Click Develop Concept to turn your idea into a concise creative brief, or write your own."
            onChange={(e) => setBrief(e.target.value)}
            onBlur={() => brief !== (book.brief ?? "") && void saveBook({ brief })}
          />
        </SectionCard>

        <SectionCard icon="target" title="Progress">
          <ul className="coloring-steps">
            <Step done={stepsDone[0]!} label="Concept written" />
            <Step done={stepsDone[1]!} label="Creative brief" />
            <Step done={stepsDone[2]!} label={`Characters (${characters.length})`} />
            <Step done={stepsDone[3]!} label={`Pages planned (${pages.length}/${book.pageCount})`} />
            <Step done={stepsDone[4]!} label={`Pages generated (${generated}/${pages.length || book.pageCount})`} />
            <Step done={stepsDone[5]!} label={`Pages approved (${approved})`} />
          </ul>
          <p className="faint" style={{ fontSize: 12, margin: "12px 0 0" }}>
            <Icon name="info" size={12} /> Nothing is generated, and no image credits are spent, until you explicitly start it.
          </p>
        </SectionCard>
      </div>
      {creating && <CreateBookModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function Step({ done, label }: { done: boolean; label: string }) {
  return (
    <li className={`coloring-step ${done ? "done" : ""}`}>
      <span className="coloring-step-dot">{done && <Icon name="check" size={11} strokeWidth={2.5} />}</span>
      <span>{label}</span>
    </li>
  );
}
